// Active-location context for the Hub.
//
// Pages query against `useActiveLocationId().activeLocationId` instead of
// the legacy "most-recent tenant" lookup. The selector in Layout writes
// here on user toggle.
//
// Defaulting:
//   - If localStorage has a valid value present in the Pro's
//     pro_locations, use it.
//   - Else pick the first row from useProLocations() (sorted desc
//     by updated_at — i.e., the location the Pro most recently used).
//   - If the stored value isn't in the Pro's pro_locations
//     (e.g., they lost access), reset to the first row.
//
// This means: a single-location Pro still gets `activeLocationId`
// populated, just to one value. The selector component decides on its
// own whether to render based on the array length, not based on
// whether activeLocationId is set.

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useProLocations } from './proLocations';

interface LocationCtx {
  activeLocationId: string | null;
  setActiveLocationId: (id: string) => void;
}

const Ctx = createContext<LocationCtx>({
  activeLocationId: null,
  setActiveLocationId: () => {},
});

const STORAGE_KEY = 'hub.activeLocationId';

export function LocationProvider({ children }: { children: ReactNode }) {
  const [activeLocationId, setStored] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const { data: locations, isLoading } = useProLocations();

  useEffect(() => {
    if (isLoading || !locations) return;
    if (locations.length === 0) return;

    const inAccess = activeLocationId
      ? locations.some((l) => l.location_id === activeLocationId)
      : false;

    if (!inAccess) {
      const next = locations[0].location_id;
      setStored(next);
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    }
  }, [locations, isLoading, activeLocationId]);

  const setActiveLocationId = (id: string) => {
    setStored(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
  };

  return (
    <Ctx.Provider value={{ activeLocationId, setActiveLocationId }}>
      {children}
    </Ctx.Provider>
  );
}

export function useActiveLocationId() {
  return useContext(Ctx);
}
